import React from 'react';
import { useNavigate } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import { normalizeProductGroupsResponse } from '../../utils/productGroups';
import '../business/Business.css';
import './ProductIndexPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';

function buildIndexPayload(products, selectedOrder) {
  const selectedSet = new Set(selectedOrder.map(String));
  const unselectedIds = products
    .map((p) => p?._id ?? p?.id)
    .filter((id) => id != null && !selectedSet.has(String(id)));

  const orderedIds = [...selectedOrder, ...unselectedIds];
  return orderedIds.map((productId, index) => ({ productId, index }));
}

export default function ProductIndexPage() {
  const navigate = useNavigate();
  const { loading, requestWithMeta } = useHttp();

  const [products, setProducts] = React.useState([]);
  const [productsLoading, setProductsLoading] = React.useState(true);
  const [selectedOrder, setSelectedOrder] = React.useState([]);
  const [submitError, setSubmitError] = React.useState(null);

  const skeletonItems = React.useMemo(() => Array.from({ length: 4 }, (_, i) => i), []);

  const fetchProducts = React.useCallback(async () => {
    setProductsLoading(true);
    setSubmitError(null);
    try {
      const res = await requestWithMeta('/operator/products', 'GET');
      if (!res?.ok) {
        setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить товары');
        return;
      }

      const normalized = normalizeProductGroupsResponse(res?.data);
      setProducts(normalized);
      setSelectedOrder([]);
    } finally {
      setProductsLoading(false);
    }
  }, [requestWithMeta]);

  React.useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const getOrderNumber = React.useCallback(
    (productId) => {
      const key = String(productId);
      const idx = selectedOrder.findIndex((id) => String(id) === key);
      return idx === -1 ? null : idx + 1;
    },
    [selectedOrder]
  );

  const toggleProduct = React.useCallback((productId) => {
    const key = String(productId);
    setSelectedOrder((prev) => {
      const idx = prev.findIndex((id) => String(id) === key);
      if (idx === -1) return [...prev, productId];
      return prev.filter((id) => String(id) !== key);
    });
  }, []);

  const selectedCount = selectedOrder.length;
  const isSubmitDisabled = loading || productsLoading || selectedCount === 0;

  async function handleSubmit() {
    setSubmitError(null);

    const payload = buildIndexPayload(products, selectedOrder);
    if (payload.length === 0) {
      setSubmitError('Нет товаров для сохранения');
      return;
    }

    const res = await requestWithMeta('/operator/products/index', 'PUT', {
      products: payload,
    });

    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось сохранить порядок');
      return;
    }

    navigate(-1);
  }

  return (
    <div className="shop-create-root product-index-page-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title="Порядок продуктов"
          subtitle="Нажимайте на товары в нужном порядке. Повторное нажатие снимает номер."
          onBack={() => navigate(-1)}
          backClassName="product-index-page-backButton"
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            <div className="product-index-page-listHeader">
              <div className="shop-create-label product-index-page-listTitle">Товары</div>
              <div className="shop-create-help shop-create-helpTight">
                {productsLoading
                  ? 'Загрузка…'
                  : selectedCount > 0
                    ? `Выбрано: ${selectedCount} из ${products.length}`
                    : `${products.length} товаров`}
              </div>
            </div>

            <div className="product-index-page-list">
              {productsLoading ? (
                skeletonItems.map((item) => (
                  <div key={item} className="product-index-page-item product-index-page-item--skeleton">
                    <div className="shop-create-skeleton product-index-page-skeletonImage" />
                    <div className="shop-create-skeleton shop-create-skeleton--text product-index-page-skeletonName" />
                    <div className="shop-create-skeleton product-index-page-skeletonBadge" />
                  </div>
                ))
              ) : products.length === 0 ? (
                <div className="shop-create-help">Товары не найдены.</div>
              ) : (
                products.map((product) => {
                  const productId = product?._id ?? product?.id;
                  const orderNumber = getOrderNumber(productId);
                  const isSelected = orderNumber != null;

                  return (
                    <button
                      key={productId}
                      type="button"
                      className={`product-index-page-item${isSelected ? ' product-index-page-item--selected' : ''}`}
                      onClick={() => toggleProduct(productId)}
                      aria-pressed={isSelected}
                      aria-label={
                        isSelected
                          ? `${product?.name || 'Товар'}, порядок ${orderNumber}. Нажмите, чтобы снять`
                          : `${product?.name || 'Товар'}. Нажмите, чтобы выбрать`
                      }
                    >
                      <img
                        className="product-index-page-image"
                        src={product?.imageUrl || ''}
                        alt=""
                        loading="lazy"
                      />

                      <div className="product-index-page-main">
                        <div className="product-index-page-name">{product?.name || 'Без названия'}</div>
                        <div className="product-index-page-meta">{product?.price} сом</div>
                      </div>

                      <div
                        className={`product-index-page-orderBadge${isSelected ? ' product-index-page-orderBadge--filled' : ''}`}
                        aria-hidden="true"
                      >
                        {isSelected ? orderNumber : ''}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </section>
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={loading ? 'Сохранение…' : 'Сохранить порядок'}
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}
